"use client";

import axios from "axios";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import Spinner from "@/ui/Spinner";
import { Button } from "@/ui/atoms/Button";
import { collectionBySlug } from "@/lib/collections";
import { type ImageSummary } from "@/ui/types";

const API_URL = process.env.NEXT_PUBLIC_MICROGRAPHER_API_URL;
const API_KEY = process.env.NEXT_PUBLIC_MICROGRAPHER_API_KEY;
const CDN = "https://d127u13u6yy1k7.cloudfront.net/static/images/products";

const imgUrl = (p: ImageSummary) =>
	`${CDN}/${p.img_name}/${p.img_name}-${p.orientation}-no-frame-no-mat.webp`;

// /collections/[slug] — a single collection "show" (DESIGN.md §3, R.3b.2). A curatorial essay,
// then the art itself in a gallery-focused grid: no prices, no purchase pressure — the pieces link
// through to the product page for anyone who wishes to acquire. Viewing over acquisition.
export default function CollectionPage({ params }: { params: { slug: string } }) {
	const collection = collectionBySlug(params.slug);
	const [products, setProducts] = useState<ImageSummary[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		axios
			.get<ImageSummary[]>(`${API_URL}/products`, { headers: { "x-api-key": API_KEY } })
			.then((res) => setProducts(Array.isArray(res.data) ? res.data : []))
			.catch((e) => console.error("Error fetching products:", e))
			.finally(() => setIsLoading(false));
	}, []);

	const pieces = useMemo(
		() => (collection ? products.filter((p) => p.series === collection.name) : []),
		[products, collection],
	);

	// Unknown slug.
	if (!collection) {
		return (
			<section className="mx-auto max-w-gallery px-5 py-section text-center sm:px-8">
				<h1 className="font-serif text-display font-light text-mg-black">Collection not found</h1>
				<div className="mt-8">
					<Button href="/collections">View all collections</Button>
				</div>
			</section>
		);
	}

	return (
		<main className="bg-bone text-mg-black">
			{/* Curatorial header + essay */}
			<section className="mx-auto max-w-gallery px-5 pt-section-sm sm:px-8">
				<Link href="/collections" className="label-caps text-ink-faint hover:text-mg-gold-deep">
					← All collections
				</Link>
				<div className="mt-8 grid gap-10 lg:grid-cols-[2fr_3fr] lg:gap-16">
					<div>
						<p className="label-caps mb-4 text-mg-gold-deep">
							{collection.forthcoming ? "Forthcoming" : `The Collection · ${pieces.length} works`}
						</p>
						<h1 className="font-serif text-display font-light leading-tight text-mg-black">
							{collection.name}
						</h1>
					</div>
					<div className="max-w-prose space-y-5 leading-relaxed text-ink-muted lg:pt-2">
						{collection.essay.map((para, i) => (
							<p key={i} className={i === 0 ? "text-lg text-mg-black/80" : ""}>
								{para}
							</p>
						))}
					</div>
				</div>
			</section>

			{/* The art — gallery grid, no prices. */}
			<section className="mx-auto max-w-gallery px-5 py-section sm:px-8">
				{isLoading ? (
					<div className="flex justify-center py-section-sm">
						<Spinner />
					</div>
				) : pieces.length ? (
					<ul className="grid grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
						{pieces.map((p, i) => (
							<li key={p.img_name} className="group">
								<Link href={`/shop/${p.img_name}`} className="block">
									<div className="relative aspect-[4/5] overflow-hidden bg-bone-deep">
										{/* ASSET: the artwork; a 4:5 gallery crop reads more like a hung print than a square. */}
										<Image
											src={imgUrl(p)}
											alt={`${p.title} — crystallized ${p.substance}`}
											fill
											priority={i < 3}
											sizes="(min-width: 1024px) 28rem, (min-width: 640px) 45vw, 100vw"
											className="object-cover transition-transform duration-700 ease-gallery group-hover:scale-[1.03]"
										/>
									</div>
									{/* Quiet caption — title + substance only; no price (viewing, not acquisition). */}
									<div className="mt-4 text-center">
										<h3 className="font-serif text-lg font-light text-mg-black">{p.title}</h3>
										<p className="label-caps mt-1 text-ink-faint">Crystallized {p.substance}</p>
									</div>
								</Link>
							</li>
						))}
					</ul>
				) : (
					<p className="py-section-sm text-center text-ink-muted">
						This collection is in preparation. Please check back soon.
					</p>
				)}
			</section>

			{/* Quiet close */}
			{pieces.length ? (
				<section className="mx-auto max-w-gallery px-5 pb-section text-center sm:px-8">
					<div className="hairline mx-auto mb-12 w-16" />
					<p className="mx-auto max-w-prose font-serif text-2xl font-light text-ink-muted">
						Every piece is a single crystallization — signed, numbered, and issued in a limited edition.
					</p>
					<div className="mt-8">
						<Button href="/collections" variant="outline">
							Explore other collections
						</Button>
					</div>
				</section>
			) : null}
		</main>
	);
}
